import React, { useCallback, useEffect, useMemo, useState } from "react";
import { listActionsByRisk, createAction, listActions, updateAction } from "./api/actions.api.js";
import { getControl, listRiskControls } from "./api/controls.api.js";
import ActionForm from "./ActionForm";
import Button from "./ui/Button";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Table from "./ui/Table";
import { spacing, typography } from "./ui/tokens";
import ModalA11yWrapper from "./ui/ModalA11yWrapper";

const statusBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  background: "#f1f5f9",
  color: "#334155",
};

const priorityBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  background: "#ede9fe",
  color: "#5b21b6",
};

const neutralBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  background: "#f3f4f6",
  color: "#4b5563",
};

const overdueBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  background: "#fee2e2",
  color: "#b91c1c",
};

const normalizeText = (value) => String(value || "").trim();

function LinkActionsModal({ open, onClose, onLink, riskId }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "All", category: "All" });

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = { limit: 50 };
        if (filters.q.trim()) params.q = filters.q.trim();
        if (filters.status !== "All") params.status = filters.status;
        if (filters.category !== "All") params.category = filters.category;
        const res = await listActions(params);
        const payload = res?.data || res || {};
        const items = Array.isArray(payload.items) ? payload.items : Array.isArray(payload) ? payload : [];
        setResults(items);
      } catch (err) {
        setError(err?.message || "Failed to load actions");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, filters]);

  if (!open) return null;

  return (
    <ModalA11yWrapper open={open} onClose={onClose} labelledBy="link-actions-title">
      <div className="modal">
        <h3 id="link-actions-title" style={{ fontSize: typography.size.lg }}>Link Actions</h3>
        <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.md }}>
          <Input
            placeholder="Search actions"
            value={filters.q}
            onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
          />
          <Select
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            {["All", "Open", "In Progress", "Blocked", "Completed"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          <Select
            value={filters.category}
            onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}
          >
            {["All", "Internal Audit", "Control", "Meeting", "Risks"].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </div>

        {error && <div style={{ color: "red" }}>{error}</div>}
        {loading && <div>Loading...</div>}

        <ul className="modal-list">
          {results
            .filter((a) => a.risk_id !== riskId)
            .map((a) => (
              <li key={a.id} className="modal-list-item">
                <div>
                  <div style={{ fontWeight: typography.weight.medium }}>{a.title || a.name}</div>
                  <div style={{ fontSize: typography.size.xs, color: "#6b7280" }}>
                    Status: {a.status || "n/a"} | Category: {a.category || "n/a"}
                  </div>
                </div>
                <Button onClick={() => onLink(a)} variant="primary">
                  Link
                </Button>
              </li>
            ))}
          {!loading && results.length === 0 && (
            <li style={{ fontStyle: "italic" }}>No actions found.</li>
          )}
        </ul>

        <div className="modal-actions">
          <Button onClick={onClose} variant="secondary">
            Close
          </Button>
        </div>
      </div>
    </ModalA11yWrapper>
  );
}

export default function RiskActionsPanel({
  riskId,
  formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "-"),
  onOpenAction,
  onActionsChange,
}) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [controlNameById, setControlNameById] = useState({});

  const load = async () => {
    if (!riskId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await listActionsByRisk(riskId);
      const nextActions = res.data || [];
      setActions(nextActions);
      if (typeof onActionsChange === "function") {
        onActionsChange(nextActions);
      }

      const riskControlsResponse = await listRiskControls(riskId);
      const riskControls = Array.isArray(riskControlsResponse?.data) ? riskControlsResponse.data : [];
      const nextControlMap = {};
      riskControls.forEach((control) => {
        if (control?.id != null && control?.name) {
          nextControlMap[String(control.id)] = control.name;
        }
      });

      const missingControlIds = Array.from(
        new Set(
          nextActions
            .map((action) => action?.control_id)
            .filter((id) => id != null && nextControlMap[String(id)] == null)
        )
      );

      if (missingControlIds.length > 0) {
        const fetchedControls = await Promise.allSettled(
          missingControlIds.map((id) => getControl(id))
        );
        fetchedControls.forEach((result, index) => {
          if (result.status !== "fulfilled") return;
          const payload = result.value?.data || result.value || {};
          const controlId = payload?.id ?? missingControlIds[index];
          const name = payload?.name;
          if (controlId != null && name) {
            nextControlMap[String(controlId)] = name;
          }
        });
      }

      setControlNameById(nextControlMap);
    } catch (err) {
      setError(err?.message || "Failed to load actions");
      if (typeof onActionsChange === "function") {
        onActionsChange([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskId]);

  const handleOpenAction = useCallback(
    (action) => {
      if (!action?.id) return;
      if (typeof onOpenAction === "function") {
        onOpenAction(action);
      }
    },
    [onOpenAction]
  );

  const handleCreate = async (payload) => {
    await createAction({ ...payload, risk_id: riskId });
    await load();
  };

  const handleLink = async (action) => {
    if (!action?.id) return;
    await updateAction(action.id, { risk_id: riskId });
    await load();
  };

  const rows = useMemo(
    () =>
      (actions || []).map((action) => {
        const dueDate = action?.due_date ? new Date(action.due_date) : null;
        const status = normalizeText(action?.status).toUpperCase();
        const isOverdue =
          dueDate &&
          !Number.isNaN(dueDate.getTime()) &&
          status !== "COMPLETED" &&
          dueDate < new Date();
        return {
          ...action,
          isOverdue,
          linkedControlName:
            action?.control_id != null
              ? controlNameById[String(action.control_id)] || "-"
              : "-",
        };
      }),
    [actions, controlNameById]
  );

  const columns = useMemo(
    () => [
      {
        id: "title",
        header: "Title",
        render: (row) => (
          <Button
            type="button"
            onClick={() => handleOpenAction(row)}
            variant="ghost"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              color: "#2563eb",
              cursor: "pointer",
              textDecoration: "underline",
              textAlign: "left",
              boxShadow: "none",
              fontSize: typography.size.sm,
            }}
          >
            {row.title || row.name || "-"}
          </Button>
        ),
      },
      {
        id: "status",
        header: "Status",
        render: (row) => <span style={statusBadgeStyle}>{normalizeText(row.status) || "Unknown"}</span>,
      },
      {
        id: "priority",
        header: "Priority",
        render: (row) =>
          normalizeText(row.priority) ? (
            <span style={priorityBadgeStyle}>{row.priority}</span>
          ) : (
            <span style={neutralBadgeStyle}>-</span>
          ),
      },
      {
        id: "owner",
        header: "Owner",
        accessor: (row) => normalizeText(row.owner) || "Unassigned",
      },
      {
        id: "due_date",
        header: "Due Date",
        accessor: (row) => formatDate(row.due_date),
      },
      {
        id: "overdue",
        header: "Overdue",
        render: (row) =>
          row.isOverdue ? <span style={overdueBadgeStyle}>Overdue</span> : <span style={neutralBadgeStyle}>No</span>,
      },
      {
        id: "linked_control",
        header: "Linked Control",
        accessor: (row) => row.linkedControlName,
      },
    ],
    [formatDate, handleOpenAction]
  );

  return (
    <div style={{ marginTop: spacing.md * 2, padding: spacing.xl, border: "1px solid #e5e7eb", borderRadius: 8 }}>
      <h3 style={{ fontSize: typography.size.lg }}>Actions for this Risk</h3>
      <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.md }}>
        <Button onClick={() => setLinkModalOpen(true)} variant="secondary">
          Link Actions
        </Button>
        <Button onClick={() => setCreateModalOpen(true)} variant="primary">
          Create Action
        </Button>
      </div>
      {error && <div style={{ color: "#b91c1c" }}>{error}</div>}
      {loading && <div>Loading actions...</div>}
      {!loading && (
        <Table
          columns={columns}
          data={rows}
          getRowKey={(row) => row.id}
          emptyState="No actions yet."
          tableStyle={{ fontSize: typography.size.sm }}
          style={{ borderRadius: 8 }}
        />
      )}

      <LinkActionsModal
        open={linkModalOpen}
        riskId={riskId}
        onClose={() => setLinkModalOpen(false)}
        onLink={async (action) => {
          await handleLink(action);
          setLinkModalOpen(false);
        }}
      />
      <ModalA11yWrapper
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        labelledBy="create-action-title"
      >
        <div className="modal">
          <h3 id="create-action-title" style={{ fontSize: typography.size.lg }}>Create Action</h3>
          <ActionForm
            onSave={async (payload) => {
              await handleCreate(payload);
              setCreateModalOpen(false);
            }}
            defaultOwner={null}
            defaultParentActionId={null}
            defaultControlId={null}
          />
          <div className="modal-actions">
            <Button onClick={() => setCreateModalOpen(false)} variant="secondary">
              Close
            </Button>
          </div>
        </div>
      </ModalA11yWrapper>
    </div>
  );
}



